import { useState } from 'react';
import { supabase } from '@/lib/supabase';
import BehaviorTimeline from './BehaviorTimeline';

interface Lead {
  id: string;
  created_at: string;
  first_name: string;
  last_name: string;
  company: string | null;
  sector: string | null;
  monthly_target: string | null;
  target_audience: string | null;
  action_type: string | null;
  notes: string | null;
  lead_context: string | null;
  session_id: string | null;
  status: string;
}

interface LeadTableProps {
  leads: Lead[];
  onStatusChange: (id: string, status: string) => void;
}

const STATUS_OPTIONS = [
  { value: 'new', label: 'Yeni', color: '#f0b429', bg: 'rgba(240,180,41,0.12)' },
  { value: 'contacted', label: 'İletişime Geçildi', color: '#60a5fa', bg: 'rgba(96,165,250,0.12)' },
  { value: 'qualified', label: 'Nitelikli', color: '#a78bfa', bg: 'rgba(167,139,250,0.12)' },
  { value: 'converted', label: 'Dönüştürüldü', color: '#34d399', bg: 'rgba(52,211,153,0.12)' },
  { value: 'lost', label: 'Kaybedildi', color: '#f87171', bg: 'rgba(248,113,113,0.12)' },
];

const getStatusStyle = (status: string) => {
  return STATUS_OPTIONS.find((s) => s.value === status) || STATUS_OPTIONS[0];
};

export default function LeadTable({ leads, onStatusChange }: LeadTableProps) {
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [behaviorLead, setBehaviorLead] = useState<{ sessionId: string; name: string } | null>(null);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [updatingId, setUpdatingId] = useState<string | null>(null);

  const filtered = leads.filter((l) => {
    const matchSearch =
      !search ||
      `${l.first_name} ${l.last_name} ${l.company || ''} ${l.sector || ''}`.toLowerCase().includes(search.toLowerCase());
    const matchStatus = filterStatus === 'all' || l.status === filterStatus;
    return matchSearch && matchStatus;
  });

  const handleStatusChange = async (id: string, status: string) => {
    setUpdatingId(id);
    await supabase.from('leads').update({ status }).eq('id', id);
    onStatusChange(id, status);
    setUpdatingId(null);
    if (selectedLead?.id === id) {
      setSelectedLead((prev) => prev ? { ...prev, status } : null);
    }
  };

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('tr-TR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <>
      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="İsim, firma veya sektör ara..."
            className="w-full pl-9 pr-4 py-2.5 rounded-xl text-sm text-white placeholder-gray-600 outline-none"
            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}
          />
        </div>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-4 py-2.5 rounded-xl text-sm text-white outline-none cursor-pointer"
          style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}
        >
          <option value="all" style={{ background: '#1a1a2e' }}>Tüm Durumlar</option>
          {STATUS_OPTIONS.map((s) => (
            <option key={s.value} value={s.value} style={{ background: '#1a1a2e' }}>{s.label}</option>
          ))}
        </select>
      </div>

      {/* Table */}
      <div
        className="rounded-2xl overflow-hidden"
        style={{ border: '1px solid rgba(255,255,255,0.07)' }}
      >
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr style={{ background: 'rgba(255,255,255,0.03)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Ad Soyad</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden md:table-cell">Sektör</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden lg:table-cell">Kaynak</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden lg:table-cell">Tarih</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Durum</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-12 text-gray-600 text-sm">
                    {leads.length === 0 ? 'Henüz lead yok. İlk lead\'i bekliyoruz!' : 'Arama kriterlerine uygun lead bulunamadı.'}
                  </td>
                </tr>
              ) : (
                filtered.map((lead) => {
                  const statusStyle = getStatusStyle(lead.status);
                  return (
                    <tr
                      key={lead.id}
                      className="transition-colors cursor-pointer"
                      style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}
                      onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.02)'; }}
                      onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
                      onClick={() => setSelectedLead(lead)}
                    >
                      <td className="px-4 py-3.5">
                        <div>
                          <p className="text-white text-sm font-semibold">{lead.first_name} {lead.last_name}</p>
                          {lead.company && <p className="text-gray-300 text-xs mt-0.5">{lead.company}</p>}
                        </div>
                      </td>
                      <td className="px-4 py-3.5 hidden md:table-cell">
                        <span className="text-gray-200 text-xs">{lead.sector || '—'}</span>
                      </td>
                      <td className="px-4 py-3.5 hidden lg:table-cell">
                        <span className="text-gray-300 text-xs">{lead.lead_context || '—'}</span>
                      </td>
                      <td className="px-4 py-3.5 hidden lg:table-cell">
                        <span className="text-gray-300 text-xs">{formatDate(lead.created_at)}</span>
                      </td>
                      <td className="px-4 py-3.5" onClick={(e) => e.stopPropagation()}>
                        <select
                          value={lead.status}
                          onChange={(e) => handleStatusChange(lead.id, e.target.value)}
                          disabled={updatingId === lead.id}
                          className="text-xs font-semibold px-2.5 py-1.5 rounded-lg outline-none cursor-pointer transition-all"
                          style={{ background: statusStyle.bg, color: statusStyle.color, border: `1px solid ${statusStyle.color}30` }}
                        >
                          {STATUS_OPTIONS.map((s) => (
                            <option key={s.value} value={s.value} style={{ background: '#1a1a2e', color: '#fff' }}>{s.label}</option>
                          ))}
                        </select>
                      </td>
                      <td className="px-4 py-3.5">
                        <div className="flex items-center gap-1.5">
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setSelectedLead(lead); }}
                            className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-500 hover:text-white transition-colors cursor-pointer"
                            style={{ background: 'rgba(255,255,255,0.05)' }}
                            title="Detay"
                          >
                            <i className="ri-eye-line text-xs" />
                          </button>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); if (lead.session_id) setBehaviorLead({ sessionId: lead.session_id, name: `${lead.first_name} ${lead.last_name}` }); }}
                            className={`w-7 h-7 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${lead.session_id ? 'text-gray-400 hover:text-yellow-400' : 'text-gray-700 cursor-not-allowed'}`}
                            style={{ background: 'rgba(255,255,255,0.05)' }}
                            title={lead.session_id ? 'Davranış Geçmişi' : 'Henüz tracking verisi yok'}
                          >
                            <i className="ri-route-line text-xs" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Behavior Timeline Modal */}
      {behaviorLead && (
        <BehaviorTimeline
          sessionId={behaviorLead.sessionId}
          leadName={behaviorLead.name}
          onClose={() => setBehaviorLead(null)}
        />
      )}

      {/* Detail Modal */}
      {selectedLead && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(8px)' }}
          onClick={() => setSelectedLead(null)}
        >
          <div
            className="relative w-full max-w-lg rounded-3xl overflow-hidden"
            style={{
              background: 'linear-gradient(135deg, #0f0f1a 0%, #1a1a2e 100%)',
              border: '1px solid rgba(240,180,41,0.2)',
              maxHeight: '85vh',
              overflowY: 'auto',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="sticky top-0 flex items-center justify-between px-6 py-4 border-b border-white/8"
              style={{ background: 'rgba(15,15,26,0.95)', backdropFilter: 'blur(12px)' }}
            >
              <div>
                <h3 className="text-white font-extrabold text-base">{selectedLead.first_name} {selectedLead.last_name}</h3>
                {selectedLead.company && <p className="text-gray-500 text-xs mt-0.5">{selectedLead.company}</p>}
              </div>
              <button
                type="button"
                onClick={() => setSelectedLead(null)}
                className="w-8 h-8 flex items-center justify-center rounded-full text-gray-500 hover:text-white hover:bg-white/10 transition-all cursor-pointer"
              >
                <i className="ri-close-line" />
              </button>
            </div>

            <div className="px-6 py-5 space-y-4">
              {/* Status changer */}
              <div>
                <p className="text-gray-500 text-xs font-semibold uppercase tracking-wider mb-2">Durum</p>
                <div className="flex flex-wrap gap-2">
                  {STATUS_OPTIONS.map((s) => (
                    <button
                      key={s.value}
                      type="button"
                      onClick={() => handleStatusChange(selectedLead.id, s.value)}
                      className="px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer transition-all whitespace-nowrap"
                      style={{
                        background: selectedLead.status === s.value ? s.bg : 'rgba(255,255,255,0.04)',
                        color: selectedLead.status === s.value ? s.color : '#6b7280',
                        border: `1px solid ${selectedLead.status === s.value ? s.color + '40' : 'rgba(255,255,255,0.08)'}`,
                      }}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                {[
                  { label: 'Sektör', value: selectedLead.sector },
                  { label: 'Aylık Hedef', value: selectedLead.monthly_target },
                  { label: 'Hedef Kitle', value: selectedLead.target_audience },
                  { label: 'Aksiyon Tipi', value: selectedLead.action_type },
                  { label: 'Kaynak', value: selectedLead.lead_context },
                  { label: 'Tarih', value: formatDate(selectedLead.created_at) },
                ].map((item) => item.value ? (
                  <div
                    key={item.label}
                    className="rounded-xl p-3"
                    style={{ background: 'rgba(255,255,255,0.04)' }}
                  >
                    <p className="text-gray-500 text-xs mb-1">{item.label}</p>
                    <p className="text-white text-sm font-semibold">{item.value}</p>
                  </div>
                ) : null)}
              </div>

              {selectedLead.notes && (
                <div className="rounded-xl p-4" style={{ background: 'rgba(255,255,255,0.04)' }}>
                  <p className="text-gray-500 text-xs mb-2">Ek Notlar</p>
                  <p className="text-gray-300 text-sm leading-relaxed">{selectedLead.notes}</p>
                </div>
              )}

              <a
                href={`https://wa.me/908508408383?text=${encodeURIComponent(`Merhaba ${selectedLead.first_name}, Smart Hizmet hakkında görüşmek istiyorum.`)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full py-3 rounded-xl font-bold text-sm cursor-pointer whitespace-nowrap"
                style={{ background: 'rgba(52,211,153,0.12)', color: '#34d399', border: '1px solid rgba(52,211,153,0.25)' }}
              >
                <i className="ri-whatsapp-line" />
                WhatsApp&apos;tan Ulaş
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
